<template>
  <div class="modal-page">
    <!-- Reason Selection Page -->
    <template v-if="page.type === 'reason_selection'">
      <form @submit.prevent="submit">
        <h3>{{ interpolateMessage(page.title) }}</h3>
        <p class="mt-4">{{ page.subtitle }}</p>

        <div class="row mt-5 ml-0 text-left">
          <h5>Vennligst velg en</h5>
          <RadioButton v-for="reason in page.reasons" :key="reason.value"
            @input="handleReasonInput(reason.value, $event)" :label="reason.label" :value="reason.value"
            :checked="formData.cancelReason === reason.value" name="cancel_reason" />
        </div>
        <textarea v-if="showOtherTextarea" class="mt-2 reason-textarea" v-model="otherReasonValue"
          @input="updateForm('otherReason', $event.target.value)" placeholder="Hva er grunnen?" />
        <div v-if="hasDisclaimer" class="modal-disclaimer">
          {{ page.disclaimer.text }}
        </div>
        <div class="modal-navigation">
          <div class="navigation-buttons">
            <button type="button" class="btn btn-secondary" @click="$emit('back')">
              Tilbake
            </button>
            <button type="button" class="btn btn-primary" @click="nextPage" :disabled="!isReasonValid">
              Fortsett
            </button>
          </div>
        </div>
      </form>
    </template>

    <!-- Date Selection Page -->
    <template v-else-if="page.type === 'date_selection'">
      <form @submit.prevent="submit">
        <h3>{{ interpolateMessage(page.title) }}</h3>
        <div class="reason-display mt-4">
          <span>Årsak: </span>
          <span class="reason-label">{{ getReasonDisplayText() }}</span>
        </div>

        <div class="mt-4">
          <label :for="page.field" class="form-label">
            <strong>{{ interpolateMessage(page.title) }}:</strong>
          </label>
          <input :id="page.field" type="date" class="form-control mt-2" :value="formData[page.field]"
            @input="updateForm(page.field, $event.target.value)" :min="minDate" required />
          <small v-if="page.helper" class="text-muted">{{ page.helper }}</small>
        </div>

        <div class="modal-navigation">
          <div class="navigation-buttons">
            <button type="button" class="btn btn-secondary" @click="$emit('back')">
              Tilbake
            </button>
            <button type="button" class="btn btn-primary" @click="nextPage" :disabled="!formData[page.field]">
              Fortsett
            </button>
          </div>
        </div>
      </form>
    </template>

    <!-- Subscription Selection Page -->
    <template v-else-if="page.type === 'subscription_selection'">
      <h3>{{ interpolateMessage(page.title) }}</h3>
      <p class="mt-3 mb-4">{{ page.subtitle }}</p>

      <!-- Show disabled state when there are pending changes -->
      <div v-if="hasPendingChanges" class="pending-changes-notice">
        <div class="notice-content">
          <i class="budicon-info notice-icon"></i>
          <div class="notice-text">
            <p><strong>Du har allerede en kommende endring på dette abonnementet.</strong></p>
            <p>Du kan avbryte endringen på abonnementsiden.</p>
          </div>
        </div>
      </div>

      <div v-else class="subscription-selection">
        <div v-for="(plan, key) in subscriptionPlans" :key="key" class="subscription-card" :class="{
          selected: formData.selectedNewSubscription === key,
          current: currentPlan === key,
          disabled: currentPlan === key,
          highlighted: plan.highlighted
        }" @click="selectSubscription(key)">
          <!-- Card Header -->
          <div class="subscription-header">
            <div class="subscription-title-section">
              <div class="subscription-title">
                <i :class="plan.iconClass"></i>
                <span class="title-text">{{ plan.title }}</span>
                <span v-if="currentPlan === key" class="current-badge">Nåværende</span>
              </div>
              <div class="subscription-subtitle">{{ plan.subtitle }}</div>
            </div>
            <div class="subscription-price-section">
              <div class="subscription-price">{{ plan.price }} kr</div>
              <div class="subscription-price-description">per måned</div>
            </div>
          </div>

          <!-- Features List -->
          <div class="subscription-features">
            <ul class="features-list">
              <li v-for="(feature, index) in plan.features" :key="index" class="feature-item"
                :class="{ active: feature.active }">
                <i class="budicon-check-ui feature-icon"></i>
                <span class="feature-text">{{ feature.text }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="modal-navigation">
        <div class="navigation-buttons">
          <button type="button" class="btn btn-secondary" @click="$emit('back')">
            Tilbake
          </button>
          <button v-if="!hasPendingChanges" type="button" class="btn btn-primary" @click="handleSubscriptionSelectionAndNext"
            :disabled="!formData.selectedNewSubscription || formData.selectedNewSubscription === currentPlan">
            Fortsett
          </button>
        </div>
      </div>
    </template>

    <!-- Confirmation Page -->
    <template v-else-if="page.type === 'confirmation'">
      <h3>{{ interpolateMessage(page.title) }}</h3>
      <p class="mt-3">{{ page.subtitle }}</p>
      <div class="confirmation-summary mt-4">
        <div v-for="field in page.summaryFields" :key="field.key"
          v-show="!field.condition || evaluateCondition(field.condition)" class="summary-item">
          <strong>{{ field.label }}:</strong>
          <span>{{ getSummaryValue(field) }}</span>
        </div>
      </div>

      <!-- Status Info Box -->
      <div v-if="page.statusInfo" class="status-info-box mt-4">
        <div v-for="(info, key) in page.statusInfo" :key="key" class="status-item">
          <strong>{{ info.label }}:</strong>
          <span>{{ getComputedValue(info.computed) }}</span>
        </div>
      </div>

      <!-- Termination Notice -->
      <div v-if="page.terminationNotice && formData.isDowngradeFlow === true" class="termination-notice mt-4">
        <p>{{ interpolateMessage(page.terminationNotice.text) }}</p>
      </div>

      <!-- Plan Change Option -->
      <div v-if="formData.selectedCancelAction === 'terminate_only' && formData.selectedNewSubscription != 'Free'"
        class="px-5 d-flex mt-4">
        <Checkbox v-model="planChangeEnabled">
          Jeg har lest og akseptert
          <a href="https://www.husleie.no/juridisk/vilkar-for-basic-og-premium/" target="_blank">Husleie.no sine
            abonnementsvilkår</a>.
        </Checkbox>
      </div>

      <!-- Confirmation Text -->
      <div v-if="page.confirmationText" class="confirmation-text mt-3">
        <p>{{ page.confirmationText }}</p>
      </div>

      <div v-if="page.warning" class="confirmation-warning mt-4">
        <i class="budicon-warning"></i>
        <p>{{ page.warning }}</p>
      </div>

      <div class="modal-navigation">
        <div class="navigation-buttons">
          <button type="button" class="btn btn-secondary" @click="$emit('back')" :disabled="isLoading">
            Tilbake
          </button>
          <button type="button" :class="dynamicButtonClass" @click="$emit('submit', page.actionMethod)"
            :disabled="isConfirmButtonDisabled">
            <i v-if="isLoading" class="budicon-loading spinner"></i>
            {{ interpolateMessage(page.actionText || 'Bekreft endringer') }}
          </button>
        </div>
      </div>
    </template>

    <!-- Success Page -->
    <template v-else-if="page.type === 'success'">
      <h3>{{ interpolateMessage(page.title) }}</h3>
      <p v-if="getSuccessSubtitle">{{ interpolateMessage(getSuccessSubtitle) }}</p>

      <div v-if="page.cancellationSummary" class="cancellation-summary mt-4">
        <p class="summary-title">{{ page.cancellationSummary.title }}</p>
        <p class="summary-reason">{{ interpolateMessage(page.cancellationSummary.reason) }}</p>
      </div>

      <div v-if="hasDisclaimer" class="modal-disclaimer mt-4">
        {{ interpolateMessage(page.disclaimer.text) }}
        <span v-if="shouldShowAdditionalDisclaimer"> {{ interpolateMessage(page.disclaimer.additionalText) }}</span>
      </div>

      <div class="modal-navigation">
        <div class="navigation-buttons">
          <button type="button" class="btn btn-primary" @click="$emit('cancel')">
            Lukk
          </button>
        </div>
      </div>
    </template>

    <!-- Deletion Confirmation Page -->
    <template v-else-if="page.type === 'deletion_confirmation'">
      <div class="deletion-wrapper">
        <p class="page-title">{{ interpolateMessage(page.title) }}</p>
        <p class="page-description">{{ page.description }}</p>

        <div class="danger-warning">
          <div>
            <i :class="page.warning.icon"></i>
            <strong>{{ page.warning.title }}</strong>
          </div>
          <div>
            <p>{{ page.warning.message }}</p>
            <ul class="plan-features">
              <li v-for="(bullet, index) in page.warning.bulletPoints" :key="index" class="warning-item">
                <span class="feature-text">{{ bullet }}</span>
              </li>
            </ul>
          </div>
        </div>

        <div class="tenancy-info">
          <p>Leieforhold som slettes:</p>
          <div class="d-flex">
            <strong>Leietaker: </strong>
            &nbsp;
            <p>{{ tenantName }}</p>
          </div>
          <div class="d-flex">
            <strong>Abonnement:</strong>
            &nbsp;
            <p>{{ subscriptionName }}</p>
          </div>
        </div>

        <div class="modal-navigation">
          <div class="navigation-buttons">
            <button type="button" class="btn btn-secondary" @click="$emit('back')" :disabled="isLoading">
              Tilbake
            </button>
            <button type="button" :class="page.buttonClass || 'btn btn-danger'"
              @click="$emit('submit', page.actionMethod)" :disabled="isLoading">
              <i v-if="isLoading" class="budicon-loading spinner"></i>
              {{ interpolateMessage(page.actionText || 'Slett leieforhold permanent') }}
            </button>
          </div>
        </div>
      </div>

    </template>

    <!-- Contact Support Page -->
    <template v-else-if="page.type === 'contact_support'">
      <div class="contact-support-wrapper">
        <h3>{{ interpolateMessage(page.title) }}</h3>
        <div class="contact-support-message mt-4">
          <p>{{ page.message }}</p>
          <p><strong>Telefon:</strong> <a :href="`tel:+47${page.phone}`">{{ page.phone }}</a></p>
          <p><strong>Epost:</strong> <a :href="`mailto:${page.email}`">{{ page.email }}</a></p>
        </div>
        <div class="modal-navigation">
          <div class="navigation-buttons">
            <button type="button" class="btn btn-primary" @click="$emit('cancel')">
              {{ page.actionText }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import RadioButton from '@modules/husleie-framework/src/components/forms/inputs/radio-buttons/RadioButton.vue';
import Checkbox from '@modules/husleie-framework/src/components/forms/inputs/checkbox/Checkbox';
import {
  getLatestIssuedAccountingOrder,
  getOrderReferenceDate,
  getScheduledDowngradeDateFromOrders,
} from '@/lib/tenancySubscriptionDates';

export default {
  name: 'ModalPageRenderer',
  components: {
    RadioButton,
    Checkbox
  },
  props: {
    page: {
      type: Object,
      required: true
    },
    formData: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    tenantName: {
      type: String,
      default: '',
    },
    subscriptionName: {
      type: String,
      default: '',
    },
    tenancySubscriptionData: {
      type: Object,
      default: () => ({})
    },
    subscriptionPlan: {
      type: String,
      default: ''
    },
    orders: {
      type: Array,
      default: () => []
    },
    tenancySubscription: {
      type: Object,
      default: () => null
    },
    effectiveDateText: {
      type: String,
      default: ''
    },
    invoiceStatusText: {
      type: String,
      default: ''
    }
  },
  emits: ['form-update', 'submit', 'back', 'cancel', 'next-page'],
  computed: {
    showOtherTextarea() {
      return this.formData.cancelReason === 'other';
    },
    otherReasonValue: {
      get() {
        return this.formData.otherReason;
      },
      set(value) {
        this.updateForm('otherReason', value);
      }
    },
    isReasonValid() {
      const isValid = (this.formData.cancelReason && this.formData.cancelReason !== 'other') ||
        (this.formData.cancelReason === 'other' && this.formData.otherReason);
      return isValid;
    },
    subscriptionPlans() {
      return this.tenancySubscriptionData || {};
    },
    currentPlan() {
      return this.subscriptionPlan || '';
    },
    hasPendingChanges() {
      return this.tenancySubscription?.pendingCancelation || this.tenancySubscription?.pendingChange;
    },
    hasDisclaimer() {
      return this.page?.disclaimer;
    },
    minDate() {
      return new Date().toISOString().split('T')[0];
    },
    latestIssuedOrder() {
      return getLatestIssuedAccountingOrder(this.orders);
    },
    dynamicButtonClass() {
      if (this.page.type === 'confirmation') {
        if (this.formData.isDowngradeFlow === true) {
          return this.page.buttonClass || 'btn btn-primary'; // Downgrade
        }
        return 'btn btn-primary'; // Upgrade / default
      }
      return this.page.buttonClass || 'btn btn-primary'; // Default or page-specified class
    },
    planChangeEnabled: {
      get() {
        return this.formData.allowPlanChange || false;
      },
      set(value) {
        this.updateForm('allowPlanChange', value);
        if (value) {
          this.updateForm('selectedPlanChange', this.getAvailablePlanChange());
        } else {
          this.updateForm('selectedPlanChange', '');
        }
      }
    },
    isConfirmButtonDisabled() {
      // Disable if loading
      if (this.isLoading) return true;

      // If plan change option is shown but not checked, disable button
      if ((this.formData.selectedCancelAction === 'terminate_only' && this.formData.selectedNewSubscription != 'Free') && !this.planChangeEnabled) {
        return true;
      }

      return false;
    },
    getSuccessSubtitle() {
      if (!this.page.subtitle) return null;
      
      // Handle conditional subtitle based on upgrade/downgrade flow
      if (typeof this.page.subtitle === 'object') {
        const isDowngrade = this.formData.isDowngradeFlow === true;
        return isDowngrade ? this.page.subtitle.downgrade : this.page.subtitle.upgrade;
      }
      
      // Handle regular string subtitle
      return this.page.subtitle;
    },
    shouldShowAdditionalDisclaimer() {
      return this.page?.disclaimer?.additionalText && 
             this.page?.disclaimer?.additionalTextCondition && 
             this.evaluateCondition(this.page.disclaimer.additionalTextCondition);
    }
  },
  methods: {
    updateForm(field, value) {
      this.$emit('form-update', { [field]: value });
    },
    submit() {
      this.$emit('submit');
    },
    handleReasonInput(reasonValue, event) {
      // Use the reasonValue we passed, not the event
      this.updateForm('cancelReason', reasonValue);
    },
    selectSubscription(planKey) {
      // Prevent selection of current plan
      if (planKey === this.currentPlan) {
        return;
      }
      this.updateForm('selectedNewSubscription', planKey);
      
      // Update isDowngradeFlow immediately when plan is selected
      const isDowngrade = this.isDowngrade(planKey, this.currentPlan);
      this.updateForm('isDowngradeFlow', isDowngrade);
    },
    nextPage() {
      this.$emit('next-page');
    },
    handleSubscriptionSelection() {
      // isDowngradeFlow is already set in selectSubscription method
      this.submit();
    },
    handleSubscriptionSelectionAndNext() {
      // isDowngradeFlow is already set in selectSubscription method
      this.nextPage();
    },
    isDowngrade(newPlan, oldPlan) {
      const planHierarchy = { 'Free': 0, 'Basic': 1, 'Premium': 2 };
      return planHierarchy[newPlan] < planHierarchy[oldPlan];
    },
    getReasonDisplayText() {
      const reasonMap = {
        // end tenancy
        'tenant_resign': 'Leietaker har avsluttet leieforholdet',
        'lease_expired': 'Leieavtalen er over',
        'tenant_moved_out': 'Leietaker har flyttet ut',
        'property_sold': 'Eiendom er solgt',
        'tenant_contract_breach': 'Leietaker har brutt leiekontrakt',
        'mutual_agreement': 'Enighet om tidlig avbrytelse av leieforhold',
        'property_renovations': 'Eiendom krever stor oppussing',
        'landlord_for_personal_use': 'Utleier trenger eiendom for personlig bruk',
        'rent_not_paid': 'Leie ikke betalt',

        // terminate sub only
        'too_expensive': 'For dyrt',
        'not_using_features': 'Bruker ikke nok funksjoner',
        'found_better_alternative': 'Fant et bedre alternativ',
        'business_closure': 'Bedriftsnedleggelse',
        'no_longer_needed': 'Ikke lenger nødvendig',
        'technical_issues': 'Tekniske problemer',
        'poor_customer_service': 'Dårlig kundeservice',
        'business_pause': 'Midlertidig bedriftspause',

        // common
        'other': this.formData.otherReason || 'Annen grunn'
      };

      return reasonMap[this.formData.cancelReason] || this.formData.cancelReason || 'Ikke valgt';
    },
    getSummaryValue(field) {
      if (field.value) return field.value;
      if (field.field) {
        const value = this.formData[field.field];
        if (field.format === 'date') {
          return this.formatDate(value) || value;
        }
        if (field.format === 'plan') {
          return `${value} (${this.subscriptionPlans[value]?.price || '0'} kr/mnd)`;
        }
        // Handle reason field to show human-readable text
        if (field.field === 'cancelReason') {
          return this.getReasonDisplayText();
        }
        return value;
      }
      if (field.computed) {
        return this.getComputedValue(field.computed);
      }
      return '';
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('no-NO');
    },
    formatNorwegianMonth(dateValue) {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return '';
      }

      return date.toLocaleDateString('nb-NO', { month: 'long' });
    },
    getEffectiveDate() {
      if (this.formData.isDowngradeFlow === true) {
        if (this.effectiveDateText) {
          return this.effectiveDateText;
        }

        const fallbackDate = getScheduledDowngradeDateFromOrders(this.orders, { fallbackToCurrentMonthEnd: false });
        if (fallbackDate) {
          return this.formatDate(fallbackDate);
        }

        const currentMonthEnd = getScheduledDowngradeDateFromOrders(this.orders, { fallbackToCurrentMonthEnd: true });
        return currentMonthEnd ? this.formatDate(currentMonthEnd) : this.formatDate(new Date());
      }
      return 'Umiddelbart';
    },
    getComputedValue(computedField) {
      // Handle computed field references that were previously accessed via parent
      if (computedField === 'getEffectiveDate') {
        return this.getEffectiveDate();
      }
      if (computedField === 'getCurrentPlanLabel') {
        const planToUse = this.formData.originalSubscriptionPlan || this.subscriptionPlan;
        return this.tenancySubscriptionData[planToUse]?.title || planToUse || 'Free';
      }
      if (computedField === 'getInvoiceStatus') {
        if (this.invoiceStatusText) {
          return this.invoiceStatusText;
        }

        const latestIssuedOrderDate = getOrderReferenceDate(this.latestIssuedOrder);
        if (!latestIssuedOrderDate) {
          return 'Neste måneds faktura er ikke sendt';
        }

        const latest = new Date(latestIssuedOrderDate);
        if (isNaN(latest.getTime())) {
          return 'Neste måneds faktura er ikke sendt';
        }

        const today = new Date();
        const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        // If latest invoice due date is in next month or later, next month's invoice is sent
        if (latest >= nextMonth) {
          return 'Neste måneds faktura er sendt';
        } else {
          return 'Neste måneds faktura er ikke sendt';
        }
      }
      if (computedField === 'getCurrentPlan') {
        return this.formData.originalSubscriptionPlan || this.subscriptionPlan || 'Free';
      }
      return '';
    },
    evaluateCondition(condition) {
      // Safe condition evaluator - no eval() to prevent code injection
      if (!condition) {
        return true;
      }

      // Handle simple string field existence check
      if (typeof condition === 'string') {
        return Boolean(this.formData[condition]);
      }

      // Handle object-based condition DSL
      if (typeof condition === 'object' && condition.field && condition.operator) {
        return this.evaluateObjectCondition(condition);
      }

      console.error('Unsupported condition format:', condition);
      return false;
    },

    evaluateObjectCondition(condition) {
      const { field, operator, value } = condition;
      const fieldValue = this.formData[field];

      // Whitelist of safe operators
      switch (operator) {
        case '===':
          return fieldValue === value;
        case '!==':
          return fieldValue !== value;
        case '==':
          return fieldValue == value;
        case '!=':
          return fieldValue != value;
        case '>':
          return fieldValue > value;
        case '<':
          return fieldValue < value;
        case '>=':
          return fieldValue >= value;
        case '<=':
          return fieldValue <= value;
        default:
          console.error('Unsupported operator:', operator);
          return false;
      }
    },
    getAvailablePlanChange() {
      const currentPlan = this.subscriptionPlan;

      if (currentPlan === 'Basic') {
        return 'Premium'; // Basic can upgrade to Premium
      } else if (currentPlan === 'Premium') {
        return 'Basic'; // Premium can downgrade to Basic
      }

      return null; // Free plan or unknown plan
    },
    getPlanDisplayName(planName) {
      return this.tenancySubscriptionData[planName]?.title || planName || 'Free';
    },
    getSubscriptionChangeMessagingContext() {
      const fromPlan = this.formData.originalSubscriptionPlan || this.subscriptionPlan || 'Free';
      const toPlan = this.formData.selectedNewSubscription || fromPlan;
      const isDowngrade = this.formData.isDowngradeFlow === true;

      return {
        fromPlan,
        toPlan,
        fromPlanName: this.getPlanDisplayName(fromPlan),
        toPlanName: this.getPlanDisplayName(toPlan),
        isDowngradeToFree: isDowngrade && String(toPlan).toLowerCase() === 'free',
        isDowngradeToPaid: isDowngrade && String(toPlan).toLowerCase() !== 'free',
        premiumInsuranceEnds:
          String(fromPlan).toLowerCase() === 'premium' && String(toPlan).toLowerCase() !== 'premium',
      };
    },
    getDowngradeConfirmationNotice() {
      const context = this.getSubscriptionChangeMessagingContext();
      const effectiveDate = this.getEffectiveDate();

      if (context.isDowngradeToFree) {
        return `Dersom du velger å avslutte ditt ${context.fromPlanName}-abonnement, vil vi fra ${effectiveDate} slutte å utstede fakturaer til din leietaker.`;
      }

      return `Dersom du velger å nedgradere ditt ${context.fromPlanName}-abonnement, vil abonnementet fra ${effectiveDate} endres til ${context.toPlanName}. Vi vil fortsatt utstede fakturaer til din leietaker.`;
    },
    getDowngradeSuccessSubtitle() {
      const context = this.getSubscriptionChangeMessagingContext();
      const lastPaidMonth = this.interpolateMessage('{lastPaidMonth}');

      if (context.isDowngradeToFree) {
        return `Du kan dra nytte av ${context.fromPlanName}-abonnementet ut ${lastPaidMonth}. Abonnementet vil bli avsluttet etter det.`;
      }

      return `Du kan dra nytte av ${context.fromPlanName}-abonnementet ut ${lastPaidMonth}. Abonnementet fortsetter deretter som ${context.toPlanName}.`;
    },
    getDowngradeAdditionalText() {
      const context = this.getSubscriptionChangeMessagingContext();
      const lastPaidMonth = this.interpolateMessage('{lastPaidMonth}');

      if (context.isDowngradeToFree) {
        return `Faktura-utkast for månedene etter ${lastPaidMonth}-perioden er slettet og vil ikke bli sendt ut.`;
      }

      return 'Fakturering fortsetter som vanlig etter endringsdatoen.';
    },
    interpolateMessage(message) {
      return message.replace(/\{(\w+)\}/g, (match, field) => {
        if (field === 'currentPlanName') {
          // Use the original subscription plan if available, otherwise fall back to current subscription plan
          const planToUse = this.formData.originalSubscriptionPlan || this.subscriptionPlan;
          return this.tenancySubscriptionData[planToUse]?.title || planToUse;
        }
        if (field === 'tenantName') {
          return this.tenantName || 'Unknown Tenant';
        }
        if (field === 'selectedTenancyEndDate') {
          return this.formatDate(this.formData.selectedTenancyEndDate) || this.formData.selectedTenancyEndDate;
        }
        if (field === 'reasonText') {
          return this.getReasonDisplayText();
        }
        if (field === 'getEffectiveDate') {
          return this.getEffectiveDate();
        }
        if (field === 'lastPaidMonth') {
          const latestIssuedOrderDate = getOrderReferenceDate(this.latestIssuedOrder);
          if (!latestIssuedOrderDate) {
            return this.formatNorwegianMonth(new Date());
          }
          return this.formatNorwegianMonth(latestIssuedOrderDate);
        }
        if (field === 'changeType') {
          // Use original subscription plan for comparison, not the updated one
          const oldPlan = this.formData.originalSubscriptionPlan || this.subscriptionPlan;
          const newPlan = this.formData.selectedNewSubscription;
          const planHierarchy = { 'Free': 0, 'Basic': 1, 'Premium': 2 };
          
          // Handle cases where both plans are in hierarchy
          if (planHierarchy.hasOwnProperty(oldPlan) && planHierarchy.hasOwnProperty(newPlan)) {
            if (planHierarchy[newPlan] > planHierarchy[oldPlan]) {
              return 'oppgradert';
            } else if (planHierarchy[newPlan] < planHierarchy[oldPlan]) {
              return 'nedgradert';
            }
          }
          
          // Individual plan fallback handling
          // If old plan is free, always upgrade
          if (oldPlan === 'Free' || oldPlan === 'free') {
            return 'oppgradert';
          }
          
          // If old plan is Premium, always downgrade
          if (oldPlan === 'Premium' || oldPlan === 'premium') {
            return 'nedgradert';
          }
          
          // If changing to Premium, always upgrade
          if (newPlan === 'Premium' || newPlan === 'premium') {
            return 'oppgradert';
          }
          
          // If changing to Free, always downgrade
          if (newPlan === 'Free' || newPlan === 'free') {
            return 'nedgradert';
          }
          
          // Default fallback
          return 'endret';
        }
        if (field === 'effectiveDate') {
          return this.getEffectiveDate();
        }
        if (field === 'newPlanPrice') {
          const newPlan = this.formData.selectedNewSubscription;
          return this.tenancySubscriptionData[newPlan]?.price || '0';
        }
        if (field === 'changeActionType') {
          return this.formData.isDowngradeFlow === true ? 'nedgradere' : 'oppgradere';
        }
        if (field === 'actionType') {
          return this.formData.isDowngradeFlow === true ? 'nedgradering' : 'oppgradering';
        }
        if (field === 'confirmationAction') {
          return this.formData.isDowngradeFlow === true ? 'Bekreft nedgradering' : 'Bekreft oppgradering';
        }
        if (field === 'premiumInsuranceNotice') {
          const context = this.getSubscriptionChangeMessagingContext();
          return context.premiumInsuranceEnds
            ? ' Merk også at forsikringen som er inkludert i abonnementet ditt blir avsluttet samme dato.'
            : '';
        }
        if (field === 'downgradeConfirmationNotice') {
          return this.getDowngradeConfirmationNotice();
        }
        if (field === 'downgradeSuccessSubtitle') {
          return this.getDowngradeSuccessSubtitle();
        }
        if (field === 'downgradeAdditionalText') {
          return this.getDowngradeAdditionalText();
        }
        return this.formData[field] || match;
      });
    }
  }
}
</script>

<style scoped lang="scss">
.modal-page {
  min-height: 200px;
  text-align: left;
}

.reason-textarea {
  height: 120px;
  width: 100%;
  padding: 6px;
}

.reason-display {
  display: flex;
  align-items: center;
  gap: 8px;

  .reason-label {
    background-color: #000;
    color: #fff;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
  }
}

.modal-disclaimer {
  border: 1px solid #2563EB33;
  border-radius: 8px;
  background-color: #EFF6FF;
  padding: 16px;
  text-align: left;
}

.confirmation-summary {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;

  .summary-item {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      color: #374151;
      margin-right: 8px;
    }
  }
}

.confirmation-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background-color: #fef3cd;
  border: 1px solid #facc15;
  border-radius: 8px;
  padding: 16px;
  color: #92400e;

  i {
    font-size: 20px;
    color: #f59e0b;
    flex-shrink: 0;
    margin-top: 2px;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
  }
}

.success-message {
  background-color: #dcfce7;
  border: 1px solid #00A37333;
  border-radius: 8px;
  padding: 24px;

  .success-icon {
    font-size: 48px;
    color: #16a34a;
    display: block;
    margin-bottom: 16px;
  }

  p {
    margin: 8px 0;
    font-size: 16px;
    line-height: 1.5;

    &:first-of-type {
      font-size: 18px;
      font-weight: 500;
    }
  }
}

.next-steps {
  h4 {
    color: #374151;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 12px;
  }

  ul {
    margin: 0;
    padding-left: 20px;

    li {
      margin-bottom: 8px;
      font-size: 14px;
      line-height: 1.5;
      color: #6b7280;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.cancellation-summary {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;

  .summary-title {
    color: #374151;
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }

  .summary-reason {
    color: #6b7280;
    font-size: 14px;
    line-height: 1.4;
    margin: 0;
  }
}

.danger-warning {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  background-color: #FEF2F2;
  border: 2px solid #FFC9C9;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  color: #C10007;

  i {
    font-size: 24px;
    color: #9F0712;
    flex-shrink: 0;
    margin-top: 2px;
  }

  div {
    flex: 1;

    strong {
      display: block;
      font-weight: 700;
      font-size: 16px;
      margin-bottom: 8px;
      color: #9F0712;
    }

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1.5;
      font-weight: 500;
    }

    ul {
      text-align: left;
    }
  }
}

.delete-confirmations {
  margin: 24px 0;

  .checkbox-wrapper {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.error-message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 12px;
  color: #dc2626;

  i {
    font-size: 16px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
  }
}

// Modal navigation styling
.modal-navigation {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.navigation-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-start;

  .btn {
    min-width: 100px;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .spinner {
    animation: spin 1s linear infinite;
    margin-right: 8px;
  }
}

.tenancy-info {
  background-color: #F9FAFB;
  border-radius: 10px;
  text-align: left;
  padding: 12px;
}

.deletion-wrapper {
  border: 1px solid #FFC9C9;
  border-radius: 10px;
  padding: 16px;
  text-align: left;

  .page-title {
    color: #E7000B;
  }

  .page-description {
    color: #767891;
  }

  .modal-navigation {
    .btn-secondary {
      flex: 1;
    }

    .btn-danger {
      flex: 2;
    }
  }
}

// Status info box styling
.status-info-box {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;

  .status-item {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      color: #374151;
      margin-right: 8px;
    }

    span {
      color: #6b7280;
    }
  }
}

// Termination notice styling (blue background)
.termination-notice {
  background-color: #eff6ff;
  border: 1px solid #3b82f6;
  border-radius: 8px;
  padding: 16px;

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    font-weight: 500;
  }
}

// Confirmation text styling
.confirmation-text {
  text-align: center;

  p {
    margin: 0;
    font-size: 13px;
    color: #6b7280;
    font-style: italic;
  }
}


// Subscription selection styling
.subscription-selection {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin: 24px 0;
}

.subscription-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 320px;

  &:hover:not(.disabled) {
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
    transform: translateY(-2px);
  }

  &.selected {
    border-color: #10b981;
    background-color: #f0fdf4;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
    background-color: #f9fafb;

    &:hover {
      transform: none;
      box-shadow: none;
      border-color: #e5e7eb;
    }
  }

  &.highlighted {
    border-color: #f59e0b;
    position: relative;

    &::before {
      content: 'Anbefalt';
      position: absolute;
      top: -12px;
      right: 20px;
      background: linear-gradient(135deg, #f59e0b, #d97706);
      color: white;
      padding: 6px 12px;
      border-radius: 16px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
    }
  }
}

.subscription-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
}

.subscription-title-section {
  flex: 1;
  min-width: 0;
}

.subscription-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;

  i {
    font-size: 24px;
    color: #6b7280;
    flex-shrink: 0;
  }

  .title-text {
    font-weight: 700;
    font-size: 20px;
    color: #1f2937;
    flex-shrink: 0;
  }

  .current-badge {
    background-color: #000;
    color: white;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 6px;
    margin-left: auto;
    white-space: nowrap;
  }
}

.subscription-subtitle {
  color: #6b7280;
  font-size: 14px;
  font-style: italic;
  line-height: 1.4;
}

.subscription-price-section {
  text-align: right;
  flex-shrink: 0;
}

.subscription-price {
  font-weight: 800;
  font-size: 28px;
  color: #059669;
  line-height: 1;
}

.subscription-price-description {
  color: #6b7280;
  font-size: 13px;
  margin-top: 2px;
  font-weight: 500;
}

.subscription-features {
  flex: 1;
  margin-top: auto;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0;

  .feature-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }

    .feature-icon {
      color: #10b981;
      font-size: 16px;
      margin-top: 2px;
      flex-shrink: 0;
    }

    .feature-text {
      color: #374151;
      font-size: 14px;
      line-height: 1.5;
      font-weight: 500;
    }

    &:not(.active) {
      opacity: 0.5;

      .feature-icon {
        color: #d1d5db;
      }

      .feature-text {
        color: #9ca3af;
      }
    }
  }
}

// Contact support page styling
.contact-support-wrapper {
  text-align: center;
  padding: 20px;

  h3 {
    color: #374151;
    margin-bottom: 20px;
  }

  .contact-support-message {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;

    p {
      margin: 0 0 12px 0;
      font-size: 16px;
      line-height: 1.5;
      color: #6b7280;

      &:last-child {
        margin-bottom: 0;
      }

      strong {
        color: #374151;
        font-weight: 600;
      }

      a {
        color: #2563eb;
        text-decoration: none;
        font-weight: 500;

        &:hover {
          color: #1d4ed8;
          text-decoration: underline;
        }
      }
    }
  }

  .modal-navigation {
    margin-top: 0;
    padding-top: 0;
    border-top: none;

    .navigation-buttons {
      justify-content: center;
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

// Pending changes notice styling
.pending-changes-notice {
  background-color: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;

  .notice-content {
    display: flex;
    align-items: flex-start;
    gap: 12px;

    .notice-icon {
      font-size: 20px;
      color: #0ea5e9;
      flex-shrink: 0;
      margin-top: 2px;
    }

    .notice-text {
      flex: 1;

      p {
        margin: 0;
        font-size: 14px;
        line-height: 1.5;
        color: #0c4a6e;

        &:first-child {
          margin-bottom: 4px;
        }

        strong {
          font-weight: 600;
        }
      }
    }
  }
}
</style>
